import {useEffect} from 'react'
import {useSelector} from 'react-redux'

import {showGlobalError} from '../../ordoro/GlobalErrorMessage.js'
import {
  getCompany,
  companyIDSelector,
  taskWebsocketsSelector,
} from '../../data/company.js'
import {getWarehouses} from '../../data/warehouses.js'
import {getSuppliers} from '../../data/suppliers.js'
import {receivedLabelInfoUpdateThrottled} from '../../data/labelInfos/index.js'
import {receivedBatchUpdateThrottled} from '../../data/batches.js'
import {receivedOrderUpdateThrottled} from '../../data/orders.js'
import loadScript from '../loadScript.js'
import {informOrderRevisionModalOfUpdate} from '../../ordoro/OrderListPage/Modals/OrderRevisionModal.js'
import {checkForNewOrdersThrottled} from '../../data/orderCounts.js'
import {getPaymentsAccountsDebounced} from '../../data/paymentAccounts.js'
import {getAccount} from '../../data/account.js'
import {getShippersDebounced} from '../../data/shippers.js'
import {getAppMessagesDebounced} from '../../data/appMessage.js'
import {getFilterConfigs} from '../../data/filterConfigs.js'
import {getAllTasksDebounced, getTasksDebounced} from '../../data/tasks.js'
import {onAuthUserWebsocketMessage} from '../../data/me.js'
import {getState} from '../../ztore.js'
import reloadApp from '../reloadApp.js'

async function connectToWebSocket(companyID) {
  if (!companyID) {
    return null
  }

  const url = window.ORDORO_BOOTSTRAP.env.PAPPY_ENV_HERMES_URL

  if (!window.io) {
    await loadScript(`${url}/socket.io/socket.io.js`)
  }

  const socket = window.io
    ? window.io(url, {
        query: {company_id: companyID},
        withCredentials: true,
      })
    : null

  if (socket) {
    socket.on('db update', (msg) => onMessage(msg))
  }

  return socket
}

async function disconnectFromWebSocket(promise) {
  if (promise) {
    const socket = await promise

    if (socket) {
      socket.close()
    }
  }
}

async function onMessage(message) {
  try {
    const payload = JSON.parse(message)

    if (payload.table === 'app_message') {
      await getAppMessagesDebounced()
    }
    if (payload.table === 'company_company') {
      await getCompany()
    }
    if (payload.table === 'misc_location') {
      await getWarehouses()
    }
    if (payload.table === 'inventory_supplier') {
      await getSuppliers()
    }
    if (payload.table === 'shipping_shipper') {
      await getShippersDebounced()
    }
    if (payload.table === 'label_info' && payload.changed_paths) {
      await receivedLabelInfoUpdateThrottled(payload)
    }
    if (
      ['orders_order_labels', 'order_revision', 'orders_order'].includes(
        payload.table,
      )
    ) {
      if (payload.table === 'orders_order') {
        await checkForNewOrdersThrottled(payload)
      }

      await receivedOrderUpdateThrottled(payload)

      if (payload.table === 'order_revision') {
        await informOrderRevisionModalOfUpdate(payload)
      }
    }
    if (payload.table === 'batch' && payload.id) {
      await receivedBatchUpdateThrottled(payload)
    }
    if (['payment_account', 'payment_record'].includes(payload.table)) {
      await getPaymentsAccountsDebounced()
    }
    if (payload.table === 'ebenezer') {
      await getAccount()
    }
    if (payload.table === 'filter_config') {
      await getFilterConfigs()
    }
    if (payload.table === 'task' && taskWebsocketsSelector(getState())) {
      await getAllTasksDebounced()
    }
    if (payload.table === 'misc_task' && taskWebsocketsSelector(getState())) {
      await getTasksDebounced()
    }
    if (payload.table === 'auth_user') {
      await onAuthUserWebsocketMessage(payload)
    }
    if (payload.action === 'reload_app') {
      // providing a message with a payload of {"action": "reload_app", "backoff": 10000}
      // will reload the app for each connected client over a 10 second period
      // not providing a backoff will default to 120 seconds
      // from the database run:
      // notify table_updated, '{"action":"reload_app","company_id":"*"}';
      reloadApp(payload.backoff || 120 * 1000)
    }
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error trying to process websocket message.',
      },
      err,
    )
  }
}

export default function WebSockets() {
  const companyID = useSelector(companyIDSelector)

  useEffect(() => {
    const promise = connectToWebSocket(companyID)

    return () => disconnectFromWebSocket(promise)
  }, [companyID])

  return null
}
